/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid,no-undef */

import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
} from "react";
import {
  createContainerFranchise,
  createFranchisePostCode,
  deleteFranchisePostCode,
  franchiseSectorStatus,
  getClassSiteList,
  getSiteList,
  updateContainerFranchise,
  getAvailableLockList,
  mapLockToContainer,
  removeLockMapping,
} from "../../../_redux/franchises/franchisesCrud";
import {
  fetchFranchisesPostCode,
  fetchFranchisesSectorPostCode,
} from "../../../_redux/franchises/franchisesActions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import ActiveDialogApi from "../../../../../../_metronic/_partials/widgets/alert/ActiveDialogApiCall";
import DeleteDialogApi from "../franchise-active-dialog/DeleteDialog";
import { Form, Modal } from "react-bootstrap";
import { InputGroup } from "react-bootstrap";
import SVG from "react-inlinesvg";
import SnackBarTool from "../../../../../../_metronic/_partials/widgets/snack-bar/SnackBar";
import { canvaCancel } from "../../../../../utils/canvaCloseFunction";
import { initIds } from "../../../../../../_metronic/_partials/controls/RightPanel";
import { toAbsoluteUrl } from "../../../../../../_metronic/_helpers";
import { useFormik } from "formik";
import { useIntl } from "react-intl";
import {
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { FixedSizeList as List } from "react-window";
import PropTypes from "prop-types";
import { getInputClasses, getApiErrorMessage } from "../../../../../utils/commonFunction";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import moment from "moment";
import { fetchContainerFranchise } from "../../../_redux/container/containerAction";

// CSS styles for virtualized options
const virtualizedStyles = `
  .virtualized-option {
    transition: background-color 0.2s ease;
  }
  .virtualized-option:hover {
    background-color: #f5f5f5 !important;
  }
  .virtualized-option.selected {
    background-color: #e3f2fd !important;
  }
`;

// Inject styles
if (
  typeof document !== "undefined" &&
  !document.getElementById("virtualized-styles-container")
) {
  const styleSheet = document.createElement("style");
  styleSheet.id = "virtualized-styles-container";
  styleSheet.innerText = virtualizedStyles;
  document.head.appendChild(styleSheet);
}

// Virtualized option component for react-window
const VirtualizedOption = forwardRef(({ index, style, data }, ref) => {
  const { options, onSelect, selectedValue } = data;
  const option = options[index];

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onSelect(option.value);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(option.value);
  };

  // Truncate long text with ellipsis
  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Check if text is long
  const isLongName = option.label && option.label.length > 50;
  const isLongSerial = option.serial && option.serial.length > 30;

  return (
    <div
      ref={ref}
      style={{
        ...style,
        padding: "8px 16px",
        cursor: "pointer",
        borderBottom: "1px solid #f0f0f0",
        backgroundColor:
          selectedValue === option.value ? "#e3f2fd" : "transparent",
        display: "flex",
        alignItems: "flex-start",
        minHeight: isLongName || isLongSerial ? "64px" : "48px",
        boxSizing: "border-box",
        transition: "background-color 0.2s ease",
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseDown={(e) => e.preventDefault()}
      className={`virtualized-option ${
        selectedValue === option.value ? "selected" : ""
      }`}
      role="option"
      aria-selected={selectedValue === option.value}
      tabIndex={0}
      aria-label={`${option.label}${
        option.serial ? `, Serial: ${option.serial}` : ""
      }`}
      title={`${option.label}${
        option.serial ? ` (Serial: ${option.serial})` : ""
      }`}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0, // Allows flex item to shrink below content size
          overflow: "hidden",
        }}
      >
        <div
          style={{
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "1.4",
            wordBreak: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: isLongName ? 2 : 1,
            WebkitBoxOrient: "vertical",
          }}
        >
          {option.label}
        </div>
        {option.serial && (
          <div
            style={{
              fontSize: "0.8rem",
              color: "#666",
              marginTop: "2px",
              wordBreak: "break-word",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Serial: {truncateText(option.serial, 30)}
          </div>
        )}
      </div>

      {/* Selection indicator */}
      {selectedValue === option.value && (
        <div
          style={{
            marginLeft: "8px",
            color: "#1976d2",
            fontSize: "16px",
            flexShrink: 0,
          }}
        >
          ✓
        </div>
      )}
    </div>
  );
});

VirtualizedOption.displayName = "VirtualizedOption";

// Custom hook for debounced search
/**
 * The value as it was `delay` ms ago, so a lookup runs on what was typed
 * rather than on every keystroke.
 *
 * @param {*} value The value to debounce
 * @param {number} delay Milliseconds to wait after the last change
 * @returns {*} The debounced value
 */
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Custom Virtualized Searchable Select Component
const VirtualizedSearchableSelect = React.memo(
  ({
    options = [],
    value,
    onChange,
    placeholder = "Search...",
    disabled = false,
    loading = false,
    height = 300,
    itemHeight = 48,
    searchPlaceholder = "Search options...",
    label = "Select Option",
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const searchInputRef = useRef(null);
    const dropdownRef = useRef(null);
    const containerRef = useRef(null);

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
      if (!debouncedSearchTerm.trim()) return options;

      const term = debouncedSearchTerm.toLowerCase();
      return options.filter(
        (option) =>
          option.label.toLowerCase().includes(term) ||
          (option.serial && option.serial.toLowerCase().includes(term))
      );
    }, [options, debouncedSearchTerm]);

    // Prepare data for virtualized list
    const listData = useMemo(
      () => ({
        options: filteredOptions,
        onSelect: (selectedValue) => {
          onChange(selectedValue);
          setIsOpen(false);
          setSearchTerm("");
        },
        selectedValue: value,
      }),
      [filteredOptions, onChange, value]
    );

    const handleToggle = useCallback(() => {
      if (disabled) return;
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
        }, 100);
      }
    }, [disabled, isOpen]);

    const handleClose = useCallback(() => {
      setIsOpen(false);
      setSearchTerm("");
    }, []);

    const handleSearchChange = useCallback((e) => {
      setSearchTerm(e.target.value);
    }, []);

    const selectedOption = options.find((opt) => opt.value === value);

    // Handle clicks outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          handleClose();
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
          document.removeEventListener("mousedown", handleClickOutside);
      }
    }, [isOpen, handleClose]);

    return (
      <div ref={containerRef} style={{ minWidth: 320, position: "relative" }}>
        <label
          htmlFor="lock-select"
          className="mt-3"
          style={{ display: "block", marginBottom: "8px" }}
        >
          {label}
        </label>

        {/* Custom Dropdown Trigger */}
        <div
          ref={dropdownRef}
          onClick={handleToggle}
          style={{
            border: "1px solid #c4c4c4",
            borderRadius: "4px",
            padding: "8px 12px",
            cursor: disabled ? "not-allowed" : "pointer",
            backgroundColor: disabled ? "#f5f5f5" : "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: "40px",
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              color: selectedOption ? "#000" : "#999",
              fontSize: "14px",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginRight: "8px",
            }}
            title={
              selectedOption
                ? selectedOption.label
                : `Select ${label.toLowerCase()}`
            }
          >
            {selectedOption
              ? selectedOption.label
              : `Select ${label.toLowerCase()}`}
          </span>
          <KeyboardArrowDownIcon
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
              flexShrink: 0,
            }}
          />
        </div>

        {/* Custom Dropdown Menu */}
        {isOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "white",
              border: "1px solid #c4c4c4",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 1000,
              maxHeight: height,
              overflow: "hidden",
              minWidth: "320px",
            }}
          >
            {/* Search Input */}
            <div style={{ padding: "8px", borderBottom: "1px solid #e0e0e0" }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    handleClose();
                  }
                }}
              />
              {loading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "8px",
                  }}
                >
                  <CircularProgress size={20} />
                </div>
              )}
            </div>

            {/* Virtualized Options List */}
            {!loading && filteredOptions.length > 0 && (
              <div
                style={{
                  height: Math.min(
                    filteredOptions.length * itemHeight,
                    height - 100
                  ),
                }}
              >
                <List
                  height={Math.min(
                    filteredOptions.length * itemHeight,
                    height - 100
                  )}
                  itemCount={filteredOptions.length}
                  itemSize={itemHeight}
                  itemData={listData}
                >
                  {VirtualizedOption}
                </List>
              </div>
            )}

            {!loading && filteredOptions.length === 0 && (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#999",
                  fontSize: "14px",
                }}
              >
                {debouncedSearchTerm
                  ? "No options found"
                  : "No options available"}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

VirtualizedSearchableSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      serial: PropTypes.string,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  height: PropTypes.number,
  itemHeight: PropTypes.number,
  searchPlaceholder: PropTypes.string,
  label: PropTypes.string,
};

/**
 * Modal for editing an existing franchise container.
 *
 * @param {Object} props
 * @param {Object} props.detailData - Franchise detail context
 * @param {Object} props.dialogCanva - Modal state and container data
 * @param {Function} props.setDialogCanva - Closes the modal
 * @param {string|number} props.fraId - Franchise id
 * @param {Object} [props.listSort] - The container table's current sort (sort_by / sort_dir), kept when the list reloads
 * @returns {JSX.Element}
 */
export function EditContainerModal({
  detailData,
  dialogCanva,
  setDialogCanva,
  fraId,
  listSort = {},
}) {
  const [snack, setSnack] = useState({ call: false, type: "", message: "" });
  const [loading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [siteList, setSiteList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [addingLock, setAddingLock] = useState(false);
  const [removingLock, setRemovingLock] = useState(false);
  const [lock_id, setLock_id] = useState("");
  const [lock_display_name, setLockDisplayName] = useState("");
  const [lock_serial_number, setLockSerialNumber] = useState("");
  const [availableLockList, setAvailableLockList] = useState([]);
  const [addLockForm, setAddLockForm] = useState(false);
  const [updateInLockForm, setUpdateInLockForm] = useState(false);
  const currentSiteId = dialogCanva?.data?.site_id;

  const dispatch = useDispatch();
  const roles = useSelector((state) => state.auth.user?.roles || []);
  const isSuperAdmin = roles?.length > 0 && roles.some((role) => role === "super-admin" || role?.name === "super-admin");

  const initialValues = {
    reference_unique_id: dialogCanva?.data?.reference_unique_id || "",
    floor: dialogCanva?.data?.floor || "",
    type: dialogCanva?.data?.type || "",
    store_vehicle: dialogCanva?.data?.store_vehicle || "",
    vehicle_access: dialogCanva?.data?.vehicle_access || "",
    available_from: dialogCanva?.data?.available_from || "",
    id: dialogCanva?.data?.id || "",
    lock_id: dialogCanva?.data?.lock_id || "",
    container_cohort: dialogCanva?.data?.container_cohort || "",
    container_room_number: dialogCanva?.data?.container_room_number || "",
    container_serial_number: dialogCanva?.data?.container_serial_number || "",
    container_owned_by: dialogCanva?.data?.container_owned_by ?? "",
    under_maintenance: dialogCanva?.data?.under_maintenance ?? false,
  };

  /**
   * Save the container, then reload the franchise's container list in the
   * table's current sort.
   *
   * @param {Object} values Form values
   * @param {Object} actions Formik helpers
   */
  const handleSubmit = (values, actions) => {
    setSnack({ call: false, type: "", message: "" });
    delete values["lock_id"];
    if (values.postcode !== "") {
      setSaving(true);
      updateContainerFranchise(values)
        .then((res) => {
          setSnack({ call: true, type: "success", message: res.data.message });
          // dispatch(fetchFranchisesPostCode(detailData, ""));
          // formik.resetForm();
          const data = {
            pageNumber: 1,
            pageSize: 20,
            franchise_id: fraId,
            ...listSort,
          };
          dispatch(fetchContainerFranchise(data));
          // dispatch(fetchFranchisesPostCode(detailData, ""));
          setTimeout(() => {
            setDialogCanva(false);
            setSaving(false);
          }, 3000);
        })
        .catch((error) => {
          setSaving(false);
          setSnack({
            call: true,
            type: "error",
            message: getApiErrorMessage(error),
          });
        });
    } else {
      formik.resetForm();
      canvaCancel("kt_container_panel_close");
    }
  };

  const handleReset = () => {};

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    onReset: handleReset,
    /**
     * Required fields on the container form, checked before submit.
     *
     * @param {Object} values Current form values
     * @returns {Object} Field name to message, empty when the form is valid
     */
    validate: (values) => {
      const errors = {};
      if (!values.reference_unique_id || !String(values.reference_unique_id).trim()) {
        errors.reference_unique_id = "Required";
      }
      if (!values.floor) {
        errors.floor = "Required";
      }
      if (!values.available_from) {
        errors.available_from = "Required";
      }
      if (!values.store_vehicle) {
        errors.store_vehicle = "Required";
      }
      if (!values.vehicle_access) {
        errors.vehicle_access = "Required";
      }
      return errors;
    },
  });

  // Transform available locks for the virtualized select
  const lockOptions = useMemo(
    () =>
      availableLockList.map((lock) => ({
        value: lock.id,
        label: lock.display_name,
        serial: lock.serial_number,
      })),
    [availableLockList]
  );

  const handleSiteList = useCallback(async () => {
    await getSiteList(fraId).then((res) => {
      setSiteList(res.data.data);
    });
  }, [fraId]);

  const handleAvailableLockList = useCallback(async () => {
    await getAvailableLockList({
      franchise_id: fraId,
      site_id: currentSiteId,
    }).then((res) => {
      setAvailableLockList(res.data.data);
    });
  }, [fraId, currentSiteId]);

  useEffect(() => {
    if (dialogCanva) {
      setLock_id(dialogCanva?.data?.lock_id || "");
      setLockDisplayName(dialogCanva?.data?.lock_display_name || "");
      setLockSerialNumber(dialogCanva?.data?.lock_serial_number || "");
      setAddLockForm(dialogCanva?.data?.lock_id ? true : false);
      handleSiteList();
      handleAvailableLockList();
    }
  }, [dialogCanva, handleAvailableLockList, handleSiteList]);

  const handleClassList = async (id) => {
    await getClassSiteList(id).then((res) => {
      setClassList(res.data.data);
    });
  };

  useEffect(() => {
    if (formik.values.site_id) {
      handleClassList(formik.values.site_id);
    }
  }, [formik.values.site_id]);

  /**
   * Close the modal, reloading the container list (in the table's current
   * sort) when a lock was changed from inside it.
   */
  const handleClose = () => {
    // setSearch("");
    formik.resetForm();
    if (updateInLockForm) {
      const data = {
        pageNumber: 1,
        pageSize: 20,
        franchise_id: fraId,
        ...listSort,
      };
      // Show loading effect
      setClosing(true);
      dispatch(fetchContainerFranchise(data));
      // Wait at least 3 seconds before closing, so user sees loading
      setTimeout(() => {
        setClosing(false);
        setDialogCanva(false);
      }, 3000);
      return;
    } else {
      setDialogCanva(false);
    }
  };

  const addLock = () => {
    setAddingLock(true);
    const data = {
      lock_id: formik.values.lock_id,
      franchise_container_id: formik.values.id,
    };
    mapLockToContainer(data)
      .then((res) => {
        const lockData = res.data.data;
        setUpdateInLockForm(true);
        setSnack({ call: true, type: "success", message: res.data.message });
        setLock_id(lockData.id);
        setLockDisplayName(lockData.display_name);
        setLockSerialNumber(lockData.serial_number);
        handleAvailableLockList();
      })
      .catch((error) => {
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error),
        });
      })
      .finally(() => setAddingLock(false));
  };

  const removeLock = () => {
    setRemovingLock(true);
    const data = {
      lock_id: formik.values.lock_id,
      franchise_container_id: formik.values.id,
    };
    removeLockMapping(data)
      .then((res) => {
        setUpdateInLockForm(true);
        setAddLockForm(false);
        setLock_id("");
        setLockDisplayName("");
        setLockSerialNumber("");
        setSnack({ call: true, type: "success", message: res.data.message });
        handleAvailableLockList();
      })
      .catch((error) => {
        setSnack({
          call: true,
          type: "error",
          message: getApiErrorMessage(error),
        });
      })
      .finally(() => setRemovingLock(false));
  };

  return (
    <Modal
      size="xl"
      fullscreen={true}
      show={!!dialogCanva}
      onHide={() => setDialogCanva(false)}
      dialogClassName="modal-90w"
      aria-labelledby="example-custom-modal-styling-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="example-modal-sizes-title-lg">Container</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/*end::Header*/}
        <>
          <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <div
              className="row mb-3"
              style={{ padding: "2%", margin: "1%", background: "#f3f3f3" }}
            >
              {/* <div className="col-md-1 mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Type</Form.Label>
                  <FormControl fullWidth>
                    <Select
                      name={`type`}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                      className={`form-control  ${getInputClasses(
                        `type`
                      )}`}
                      IconComponent={(props) => (
                        <i {...props}>
                          <KeyboardArrowDownIcon />
                        </i>
                      )}
                      inputProps={{ "aria-label": "Without label" }}
                      displayEmpty
                      {...formik.getFieldProps(`type`)}

                      //   onBlur={handleBlur}
                      // value={values.country_id_id}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="ELE">ELE</MenuItem>
                      <MenuItem value="NON">NON</MenuItem>
                      <MenuItem value="POTENTIAL">POTENTIAL</MenuItem>
                    </Select>
                  </FormControl>
                  {formik.touched.country_id && formik.errors.country_id ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        {formik.errors.country_id}
                      </div>
                    </div>
                  ) : null}
                </Form.Group>
              </div> */}
              <div className="col-md-2 mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Floor</Form.Label>
                  <FormControl fullWidth className={formik.errors.floor ? "is-invalid" : ""}>
                    <Select
                      name="floor"
                      value={formik.values.floor ?? ""}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                      className={`form-control  ${getInputClasses(`floor`)}`}
                      IconComponent={(props) => (
                        <i {...props}>
                          <KeyboardArrowDownIcon />
                        </i>
                      )}
                      inputProps={{ "aria-label": "Without label" }}
                      displayEmpty
                      onChange={formik.getFieldProps("floor").onChange}
                      onBlur={formik.getFieldProps("floor").onBlur}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="G">Ground</MenuItem>
                      <MenuItem value="1F">1st</MenuItem>
                      <MenuItem value="2F">2nd</MenuItem>
                    </Select>
                  </FormControl>
                  {formik.errors.floor ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block text-danger">
                        {formik.errors.floor}
                      </div>
                    </div>
                  ) : null}
                </Form.Group>
              </div>
              {/* <div className="col-md-2 mb-3">
                          <Form.Group className="mb-0">
                            <Form.Label>Currently on site</Form.Label>
                            <FormControl fullWidth>
                              <Select
                                name="country_id"
                                MenuProps={{
                                  anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                  },
                                  transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                  },
                                  getContentAnchorEl: null,
                                }}
                                className={`form-control  ${getInputClasses(
                                  "country_id"
                                )}`}
                                IconComponent={(props) => (
                                  <i {...props}>
                                    <KeyboardArrowDownIcon />
                                  </i>
                                )}
                                inputProps={{ "aria-label": "Without label" }}
                                displayEmpty
                                {...formik.getFieldProps("country_id")}
                              >
                                <MenuItem value="">Select</MenuItem>
                              </Select>
                            </FormControl>
                            {formik.touched.country_id &&
                            formik.errors.country_id ? (
                              <div className="fv-plugins-message-container">
                                <div className="fv-help-block">
                                  {formik.errors.country_id}
                                </div>
                              </div>
                            ) : null}
                          </Form.Group>
                        </div> */}
              <div className="col-md-2 mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Store vehicle</Form.Label>
                  <FormControl fullWidth>
                    <Select
                      name={`store_vehicle`}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                      className={`form-control  ${getInputClasses(
                        `store_vehicle`,
                      )}`}
                      IconComponent={(props) => (
                        <i {...props}>
                          <KeyboardArrowDownIcon />
                        </i>
                      )}
                      inputProps={{ "aria-label": "Without label" }}
                      displayEmpty
                      {...formik.getFieldProps(`store_vehicle`)}

                      //   onBlur={handleBlur}
                      // value={values.country_id_id}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="YES">Yes</MenuItem>
                      <MenuItem value="NO">No</MenuItem>
                    </Select>
                  </FormControl>
                  {formik.errors.store_vehicle ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        {formik.errors.store_vehicle}
                      </div>
                    </div>
                  ) : null}
                </Form.Group>
              </div>

              {/* <div className="col-2 mb-4">
                          <Form.Group className="mb-0">
                            <Form.Label>Deposite Value</Form.Label>
                            <FormControl fullWidth>
                              <input
                                type="text"
                                name="price_increase_value"
                                placeholder={"£"}
                                label="External Name"
                                className={`form-control  `}
                                // max={3}
                                // maxLength="3"
                                {...formik.getFieldProps(
                                  "price_increase_value"
                                )}
                              />
                            </FormControl>
                          </Form.Group>
                          {formik.touched.price_increase_value &&
                          formik.errors.price_increase_value ? (
                            <div className="fv-plugins-message-container">
                              <div className="fv-help-block">
                                {formik.errors.price_increase_value}
                              </div>
                            </div>
                          ) : null}
                        </div> */}

              <div className="col-md-2 mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Vehicle Access</Form.Label>
                  <FormControl fullWidth>
                    <Select
                      name={`vehicle_access`}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                      className={`form-control  ${getInputClasses(
                        `vehicle_access`,
                      )}`}
                      IconComponent={(props) => (
                        <i {...props}>
                          <KeyboardArrowDownIcon />
                        </i>
                      )}
                      inputProps={{ "aria-label": "Without label" }}
                      displayEmpty
                      {...formik.getFieldProps(`vehicle_access`)}

                      //   onBlur={handleBlur}
                      // value={values.country_id_id}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="YES">Yes</MenuItem>
                      <MenuItem value="NO">No</MenuItem>
                    </Select>
                  </FormControl>
                  {formik.errors.vehicle_access ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        {formik.errors.vehicle_access}
                      </div>
                    </div>
                  ) : null}
                </Form.Group>
              </div>
              <div className="col-2 mb-4">
                <Form.Group className="mb-0">
                  <Form.Label>Unique&nbsp;ID</Form.Label>
                  <FormControl fullWidth>
                    <input
                      type="text"
                      name="reference_unique_id"
                      placeholder={"ID"}
                      label="ID"
                      className={`form-control ${formik.errors.reference_unique_id ? "is-invalid" : ""}`}
                      value={formik.values.reference_unique_id ?? ""}
                      onChange={formik.getFieldProps("reference_unique_id").onChange}
                      onBlur={formik.getFieldProps("reference_unique_id").onBlur}
                    />
                  </FormControl>
                </Form.Group>
                {formik.errors.reference_unique_id ? (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block text-danger">
                      {formik.errors.reference_unique_id}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="col-md-4 mb-5">
                <Form.Group className="mb-0">
                  <Form.Label>Available from</Form.Label>
                  <InputGroup className={`calendr ${formik.errors.available_from ? "is-invalid" : ""}`}>
                    <DatePicker
                      fullWidth
                      fixedHeight
                      className="form-control"
                      wrapperClassName="datepicker"
                      selected={
                        formik.values.available_from
                          ? moment(
                              formik.values.available_from,
                              "YYYY-MM-DD",
                            ).toDate()
                          : null
                      }
                      dateFormat="dd-MM-Y"
                      placeholderText="Select From Date"
                      onChange={(fromDate) => {
                        formik.setFieldValue(
                          "available_from",
                          fromDate ? moment(fromDate).format("YYYY-MM-DD") : "",
                        );
                      }}
                      minDate={new Date()}
                    />
                    <CalendarTodayIcon />
                  </InputGroup>
                  {formik.errors.available_from ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block text-danger">
                        {formik.errors.available_from}
                      </div>
                    </div>
                  ) : null}
                </Form.Group>
              </div>
              {isSuperAdmin && (
              <div className="col-md-2 mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Cohort</Form.Label>
                  <FormControl fullWidth>
                    <input
                      type="text"
                      name="container_cohort"
                      placeholder="Cohort"
                      className="form-control"
                      value={formik.values.container_cohort}
                      {...formik.getFieldProps("container_cohort")}
                    />
                  </FormControl>
                </Form.Group>
              </div>
              )}
              <div className="col-md-2 mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Room Number</Form.Label>
                  <FormControl fullWidth>
                    <input
                      type="text"
                      name="container_room_number"
                      placeholder="Room Number"
                      className="form-control"
                      value={formik.values.container_room_number}
                      {...formik.getFieldProps("container_room_number")}
                    />
                  </FormControl>
                </Form.Group>
              </div>
              <div className="col-md-2 mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Serial Number</Form.Label>
                  <FormControl fullWidth>
                    <input
                      type="text"
                      name="container_serial_number"
                      placeholder="Serial Number"
                      className="form-control"
                      value={formik.values.container_serial_number}
                      {...formik.getFieldProps("container_serial_number")}
                    />
                  </FormControl>
                </Form.Group>
              </div>
              {isSuperAdmin && (
              <div className="col-3 mb-4">
                <Form.Group className="mb-0">
                  <Form.Label>Owned By</Form.Label>
                  <FormControl fullWidth>
                    <Select
                      name="container_owned_by"
                      value={formik.values.container_owned_by ?? ""}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        getContentAnchorEl: null,
                      }}
                      className={`form-control ${getInputClasses(
                        "container_owned_by",
                      )}`}
                      IconComponent={(props) => (
                        <i {...props}>
                          <KeyboardArrowDownIcon />
                        </i>
                      )}
                      inputProps={{ "aria-label": "Without label" }}
                      displayEmpty
                      onChange={formik.getFieldProps("container_owned_by").onChange}
                      onBlur={formik.getFieldProps("container_owned_by").onBlur}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value={0}>Not Owned</MenuItem>
                      <MenuItem value={1}>Owned by ESI</MenuItem>
                    </Select>
                  </FormControl>
                </Form.Group>
              </div>
              )}
              <div className="col-md-3 mb-3 pt-6 d-flex align-items-center">
                <Form.Group className="mb-0">
                  <Form.Check
                    type="checkbox"
                    id="under_maintenance"
                    label="Under maintenance"
                    checked={formik.values.under_maintenance}
                    onChange={(e) =>
                      formik.setFieldValue("under_maintenance", e.target.checked)
                    }
                  />
                </Form.Group>
              </div>
            </div>
          </form>
          <div>
            {!addLockForm && lock_id === "" && (
              <div
                className="row mb-3"
                style={{ padding: "2%", margin: "1%", background: "#f3f3f3" }}
              >
                {availableLockList.length > 0 ? (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setAddLockForm(true)}
                  >
                    Map Lock
                  </button>
                ) : (
                  <div>No available lock</div>
                )}
              </div>
            )}
            {addLockForm && (
              <div
                className="row mb-3"
                style={{ padding: "2%", margin: "1%", background: "#f3f3f3" }}
              >
                {lock_id === "" ? (
                  <>
                    <div className="col-md-6 mb-3">
                      <VirtualizedSearchableSelect
                        options={lockOptions}
                        value={formik.values.lock_id}
                        onChange={(value) =>
                          formik.setFieldValue("lock_id", value)
                        }
                        disabled={loading}
                        loading={loading}
                        searchPlaceholder="Search locks by name or serial..."
                        height={300}
                        itemHeight={48}
                        label="Available Locks"
                      />
                    </div>
                    <div
                      className="col-md-3 mb-3"
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                      }}
                    >
                      <button
                        className="btn btn-primary btn-md"
                        style={{ marginTop: "35px" }}
                        onClick={() => addLock()}
                        disabled={formik.values.lock_id === "" || addingLock}
                      >
                        {addingLock ? (
                          <>
                            <CircularProgress size={16} />
                            <> </>
                            Adding...
                          </>
                        ) : (
                          "Confirm"
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="col-md-12 mb-3">
                    <div
                      className="d-flex flex-row gap-3"
                      style={{ gap: "20px", alignItems: "center" }}
                    >
                      <div className="form-group mb-0">
                        <Form.Label>Lock Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={lock_display_name}
                          readOnly
                          className="bg-light form-control"
                        />
                      </div>
                      <div className="form-group mb-0">
                        <Form.Label>Lock Serial Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={lock_serial_number}
                          readOnly
                          className="bg-light form-control"
                        />
                      </div>
                      <button
                        className="btn btn-primary btn-md"
                        style={{ marginTop: "25px" }}
                        onClick={() => removeLock()}
                        disabled={removingLock}
                      >
                        {removingLock ? (
                          <>
                            <CircularProgress size={16} />
                            <> </>
                            Removing...
                          </>
                        ) : (
                          "Remove"
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      </Modal.Body>
      {snack.call ? <SnackBarTool {...snack} /> : ""}
      <Modal.Footer>
        <div>
          <button
            type="button"
            onClick={() => handleClose()}
            className="btn btn-light btn-elevate"
          >
            {closing ? (
              <>
                <CircularProgress size={16} />
                <> </>
                Closing...
              </>
            ) : (
              "Close"
            )}
          </button>
          <> </>

          <button
            type="button"
            className="btn btn-light btn-elevate"
            onClick={() => formik.submitForm()}
          >
            {saving ? (
              <>
                <CircularProgress size={16} />
                <> </>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </Modal.Footer>
      {/* <ActiveDialogApi
        dialog={dialogSector}
        setDialog={setDialogSector}
        title={"Sector"}
        handleChange={handleChange}
      /> */}
      {/* <DeleteDialogApi
        dialog={dialog}
        setDialog={setDialog}
        title={"Postcode"}
        handleChange={handleDelete}
      /> */}
      {/* {sectorDa.data ? (
        <>
          <EditSector
            type={sectorDa.type}
            data={sectorDa?.data}
            setSectorDa={setSectorDa}
          />
          <div
            className="offcanvas-overlay"
            onClick={() => setSectorDa({ type: "" })}
          ></div>
        </>
      ) : (
        ""
      )} */}
    </Modal>
  );
}
